import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Typography, Alert, Tabs, Tab, TableContainer, Box, Button
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import styled, { keyframes } from 'styled-components';
import Pagination from '../../utils/Pagination.js';
import recruiterTheme, { FONT_BODY, TYPE, RADIUS, SHADOW } from '../../styles/recruiterTheme';

// MUI icons
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';

const getAllJobDescriptionsData = process.env.REACT_APP_JOB_DESCRIPTION_DATA_BY_USER_API;
const getInterviewDetailsByCandidateId = process.env.REACT_APP_GET_INTERVIEW_DETAILS_BY_CANDIDATE_ID_API;
const getCandidateStatusCountsByJobId = process.env.REACT_APP_GET_CANDIDATE_STATUS_COUNTS_BY_JOB_ID_API;
const candidateProfileByJdId = process.env.REACT_APP_CANDIDATE_PROFILE_BY_JDID_API;

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Wrapper ──────────────────────────────────────────────────────────────────
// Sits directly on the host dashboard's canvas, which provides the page padding.
const ReportsWrapper = styled.div`
  font-family: ${FONT_BODY};
`;

// ─── Stat Cards ───────────────────────────────────────────────────────────────
// 7 cards: 4 + 3 on wide screens, 2 per row below 1200px; rows stretch so cards stay equal height.
const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  gap: 16px;
  @media (min-width: 1200px) { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.4s ease both;
`;

const StatCard = styled.div`
  background: ${p => p.gradient || 'linear-gradient(135deg,#6750A4,#4F378B)'};
  border-radius: ${RADIUS.md}px;
  padding: 16px;
  min-height: 88px;
  height: 100%;
  box-sizing: border-box;
  color: #fff;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 4px 16px ${p => p.shadow || 'rgba(103,80,164,0.26)'};
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: default;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 24px ${p => p.shadow || 'rgba(103,80,164,0.36)'}; }
  &::after {
    content: '';
    position: absolute;
    top: -24px; right: -24px;
    width: 72px; height: 72px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
`;

const StatText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatNum = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;

const StatLbl = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  opacity: 0.88;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Small secondary line under a stat label (e.g. "6 active").
const StatSub = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 500;
  line-height: 1.25;
  opacity: 0.75;
`;

const StatIconWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  opacity: 0.35;
  svg { font-size: 32px; }
`;

// ─── Overview Section ─────────────────────────────────────────────────────────
const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
  animation: ${fadeSlideIn} 0.45s ease 0.05s both;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const OverviewCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.md}px !important;
    padding: 24px !important;
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid #EDE7F6;
    background: #fff !important;
    height: 100%;
    box-sizing: border-box;
  }
`;

const CardTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin-bottom: 16px !important;
    padding-bottom: 12px !important;
    border-bottom: 2px solid #F0EAF9;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

// ─── Inline progress bar ──────────────────────────────────────────────────────
const MiniBarWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const MiniBarLabel = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 600;
  color: #49454F;
  width: 112px;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MiniBar = styled.div`
  flex: 1;
  height: 8px;
  border-radius: ${RADIUS.pill}px;
  background: #F0EAF9;
  overflow: hidden;
`;

const MiniBarFill = styled.div`
  height: 100%;
  border-radius: ${RADIUS.pill}px;
  background: ${p => p.color || '#6750A4'};
  width: ${p => p.pct}%;
  transition: width 0.5s ease;
`;

const MiniBarVal = styled.span`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.caption}px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${p => p.color || '#6750A4'};
  min-width: 32px;
  text-align: right;
`;

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const StyledTabsWrap = styled.div`
  background: #fff;
  border-radius: ${RADIUS.md}px;
  padding: 4px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 24px;
  box-shadow: 0 2px 12px rgba(79,55,139,0.07);
  border: 1px solid #EDE7F6;
  min-width: 0;
`;

// ─── Table components ─────────────────────────────────────────────────────────
const TableCard = styled(Paper)`
  && {
    border-radius: ${RADIUS.md}px !important;
    overflow: hidden;
    box-shadow: ${SHADOW.card} !important;
    border: 1px solid #EDE7F6;
    margin-bottom: 0;
    animation: ${fadeSlideIn} 0.4s ease both;
  }
`;

const HeadCell = styled(TableCell)`
  && {
    background: linear-gradient(135deg, #4F378B 0%, #6750A4 100%);
    color: #fff !important;
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.overline}px !important;
    letter-spacing: 0.06em;
    line-height: 1.25;
    text-transform: uppercase;
    padding: 12px 16px !important;
    white-space: nowrap;
    vertical-align: middle;
  }
`;

const BodyRow = styled(TableRow)`
  && {
    transition: background 0.15s;
    &:nth-child(even) td { background: #FDFBFF; }
    &:hover td { background: #F3EEF9 !important; }
  }
`;

const BodyCell = styled(TableCell)`
  && {
    font-family: ${FONT_BODY} !important;
    font-size: ${TYPE.small}px !important;
    font-weight: 500 !important;
    line-height: 1.5;
    color: #3D2B6B !important;
    padding: 12px 16px !important;
    border-bottom: 1px solid #F0EAF9 !important;
    vertical-align: middle;
  }
`;

// Long free-text cells (email, remarks, interviewers): single line with tooltip.
const CellText = styled.span`
  display: block;
  max-width: ${p => p.max || 220}px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// ─── Status chip ──────────────────────────────────────────────────────────────
const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 24px;
  box-sizing: border-box;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.overline}px;
  white-space: nowrap;
  background: ${p => p.bg};
  color: ${p => p.fg};
`;

const StatusDot = styled.span`
  width: 6px; height: 6px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;

const getStatusChip = (status) => {
  if (!status) return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
  if (['Interview_Scheduled', 'Round2_Scheduled'].includes(status)) return { bg: '#E3F2FD', fg: '#1565C0', dot: '#1976D2' };
  if (['Selected', 'ScreeningTest_Passed', 'Recruiter_Accepted'].includes(status)) return { bg: '#E6F4EA', fg: '#2D7D46', dot: '#2D7D46' };
  if (['Rejected', 'ScreeningTest_Failed', 'Recruiter_Rejected', 'HiringManager_Rejected'].includes(status)) return { bg: '#FDECEA', fg: '#B3261E', dot: '#E53935' };
  if (status === 'NewCandidate') return { bg: '#FFF8E1', fg: '#E65100', dot: '#FFB300' };
  return { bg: '#EDE7F6', fg: '#4F378B', dot: '#7C5CBF' };
};

// ─── Metric cell ─────────────────────────────────────────────────────────────
const MetricCell = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 24px;
  box-sizing: border-box;
  font-family: ${FONT_BODY};
  font-weight: 700;
  font-size: ${TYPE.small}px;
  font-variant-numeric: tabular-nums;
  color: ${p => p.color || '#6750A4'};
  background: ${p => p.bg || '#EDE7F6'};
  border-radius: ${RADIUS.sm}px;
  padding: 0 8px;
`;

// Section header: accent bar + title (same pattern as the rest of the workspace).
const SectionTitle = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    margin-bottom: 16px !important;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    &::before {
      content: '';
      width: 4px;
      height: 20px;
      border-radius: 2px;
      background: #6750A4;
      flex-shrink: 0;
    }
  }
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: ${RADIUS.pill}px;
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 600;
  background: #EDE7F6;
  color: #4F378B;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 24px;
  background: #fff;
  border-radius: ${RADIUS.md}px;
  border: 1px solid #EDE7F6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const emptyTitleSx = { fontFamily: FONT_BODY, fontSize: TYPE.cardTitle, fontWeight: 700, lineHeight: 1.25, color: '#C4B0E8' };
const emptyTextSx = { fontFamily: FONT_BODY, fontSize: TYPE.body, lineHeight: 1.5, color: '#B0A0D0' };

// Numeric/metric columns are centred (short pills); everything else is left-aligned.
const CENTER_COLS = new Set(['#', 'Exp', 'Positions', 'Shortlisted', 'In Progress', 'On Hold', 'Applications', 'Rejected']);
const headAlign = (head) => (CENTER_COLS.has(head) ? 'center' : 'left');

// ─────────────────────────────────────────────────────────────────────────────
const readStoredUser = () => {
  try { return JSON.parse(localStorage.getItem('rbotUser') || 'null') || {}; }
  catch { return {}; }
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const SCHEDULED_STATUSES = ['Interview_Scheduled', 'Interview_Rescheduled', 'Round2_Scheduled'];
const REJECTED_STATUSES = [
  'Rejected', 'ScreeningTest_Failed', 'ReScreeningTest_Failed', 'Recruiter_Rejected', 'HiringManager_Rejected',
  'TestPassed_Rejected', 'ReTestPassed_Rejected', 'Round1_Rejected', 'Interviewer_Rejected',
];

const Reports = () => {
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [interviewers, setInterviewers] = useState({});
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [errorJobs, setErrorJobs] = useState(null);
  const [errorCandidates, setErrorCandidates] = useState(null);
  const [failedJobIds, setFailedJobIds] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [reloadKey, setReloadKey] = useState(0);
  // Each load bumps this; responses from an older load are dropped.
  const loadSeqRef = useRef(0);

  const statusFilter = SCHEDULED_STATUSES;

  // Filter first, then paginate, then group — so the page controls actually page the rows.
  const scheduledCandidates = candidates.filter(c => statusFilter.includes(c?.overallStatus));

  const itemsPerPage = 10;
  const [candidatePage, setCandidatePage] = useState(1);
  const [jobPage, setJobPage] = useState(1);
  const [filteredPage, setFilteredPage] = useState(1);
  const [jdMetricsPage, setJdMetricsPage] = useState(1);

  const { totalPages, currentPage, totalItems } = (() => {
    let total = 0;
    let current = 1;
  
    if (activeTab === 0) {
      total = candidates.length;
      current = candidatePage;
    } else if (activeTab === 1) {
      total = jobDescriptions.length;
      current = jobPage;
    } else if (activeTab === 2) {
      total = scheduledCandidates.length;
      current = filteredPage;
    } else if (activeTab === 3) {
      total = jobDescriptions.length;
      current = jdMetricsPage;
    }
  
    return {
      totalPages: Math.ceil(total / itemsPerPage) || 1, // Ensure at least 1 page
      currentPage: current,
      totalItems: total
    };
  })();

  // Keep every tab's page in range when its data shrinks (reload, fewer results).
  useEffect(() => {
    const clamp = (n) => (p) => Math.min(p, Math.max(Math.ceil(n / itemsPerPage), 1));
    setCandidatePage(clamp(candidates.length));
    setJobPage(clamp(jobDescriptions.length));
    setJdMetricsPage(clamp(jobDescriptions.length));
    setFilteredPage(clamp(scheduledCandidates.length));
  }, [candidates.length, jobDescriptions.length, scheduledCandidates.length]);

  const handlePrevPage = () => {
    if (activeTab === 0) setCandidatePage((p) => Math.max(p - 1, 1));
    if (activeTab === 1) setJobPage((p) => Math.max(p - 1, 1));
    if (activeTab === 2) setFilteredPage((p) => Math.max(p - 1, 1));
    if (activeTab === 3) setJdMetricsPage((p) => Math.max(p - 1, 1));
  };

  const handleNextPage = () => {
    if (activeTab === 0) setCandidatePage((p) => Math.min(p + 1, totalPages));
    if (activeTab === 1) setJobPage((p) => Math.min(p + 1, totalPages));
    if (activeTab === 2) setFilteredPage((p) => Math.min(p + 1, totalPages));
    if (activeTab === 3) setJdMetricsPage((p) => Math.min(p + 1, totalPages));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) setCandidatePage(1);
    if (newValue === 1) setJobPage(1);
    if (newValue === 2) setFilteredPage(1);
    if (newValue === 3) setJdMetricsPage(1);
  };

  const currentProfiles = candidates.slice((candidatePage - 1) * itemsPerPage, candidatePage * itemsPerPage);
  const currentJobDescriptions = jobDescriptions.slice((jobPage - 1) * itemsPerPage, jobPage * itemsPerPage);
  const currentJDS = jobDescriptions.slice((jdMetricsPage - 1) * itemsPerPage, jdMetricsPage * itemsPerPage);
  const scheduledPageStart = (filteredPage - 1) * itemsPerPage;
  const filteredCandidates = scheduledCandidates
    .slice(scheduledPageStart, scheduledPageStart + itemsPerPage)
    .reduce((result, candidate) => {
      const key = candidate.jdID || '—';
      if (!result[key]) result[key] = [];
      result[key].push(candidate);
      return result;
    }, {});

  const handleRetry = () => setReloadKey(k => k + 1);

  const fetchInterviewDetailsByCandidateID = useCallback(async (candidateID) => {
    try {
      const response = await axios.get(`${getInterviewDetailsByCandidateId}/${candidateID}`);
      return response?.data || null;
    } catch {
      return null;
    }
  }, []);

  // Returns { candidates, interviewDetails } or throws; no state writes so a stale run can't clobber a newer one.
  const fetchCandidatesByJobID = async (jobId) => {
    let candidatesData = [];
    try {
      const response = await axios.get(`${candidateProfileByJdId}/${jobId}`);
      candidatesData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.candidates) ? response.data.candidates : [];
    } catch (error) {
      if (error.response?.status === 404) return { candidates: [], interviewDetails: {} };
      throw error;
    }
    const interviews = await Promise.all(candidatesData.map(c => fetchInterviewDetailsByCandidateID(c?._id)));
    const interviewDetails = {};
    candidatesData.forEach((candidate, index) => {
      interviewDetails[candidate._id] = interviews[index] ?? {
        interviewers: ['NA'], dateAndTime: 'NA', meetLink: 'NA', recruiter: 'NA', interviewStatus: 'Not Scheduled',
      };
    });
    return { candidates: candidatesData, interviewDetails };
  };

  useEffect(() => {
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;
    const storedUser = readStoredUser();
    const email = localStorage.getItem('email') || storedUser.email;

    const load = async () => {
      setLoadingJobs(true);
      setLoadingCandidates(true);
      setErrorJobs(null);
      setErrorCandidates(null);
      setFailedJobIds([]);

      if (!email) {
        setErrorJobs('Could not determine the signed-in user. Please log in again.');
        setLoadingJobs(false); setLoadingCandidates(false);
        return;
      }

      let jds = [];
      try {
        const response = await axios.get(`${getAllJobDescriptionsData}=${encodeURIComponent(email)}`);
        if (isStale()) return;
        jds = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        if (isStale()) return;
        if (error.response?.status !== 404) {
          setErrorJobs(error.response?.data?.message || error.response?.data?.error || 'Error fetching job descriptions');
        }
      }
      setJobDescriptions(jds);
      setLoadingJobs(false);
      if (jds.length === 0) {
        setCandidates([]); setStatusCounts({}); setLoadingCandidates(false);
        return;
      }

      // Status counts load independently; a failure just leaves that JD's metrics at 0.
      Promise.all(jds.map(jd =>
        axios.get(`${getCandidateStatusCountsByJobId}/${jd._id}`)
          .then(r => [jd._id, r.data || {}])
          .catch((error) => { console.error('Error fetching status counts:', error); return [jd._id, {}]; })
      )).then(entries => { if (!isStale()) setStatusCounts(Object.fromEntries(entries)); });

      const results = await Promise.allSettled(jds.map(jd => fetchCandidatesByJobID(jd._id)));
      if (isStale()) return;
      const all = [];
      const details = {};
      const failed = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') { all.push(...r.value.candidates); Object.assign(details, r.value.interviewDetails); }
        else failed.push(jds[i]?.jdID || jds[i]?._id);
      });
      setCandidates(all);
      setInterviewers(details);
      setFailedJobIds(failed);
      if (failed.length === jds.length) setErrorCandidates('Error fetching candidates');
      setLoadingCandidates(false);
    };
    load();
  }, [reloadKey, fetchInterviewDetailsByCandidateID]);

  // ─── Computed overview stats ──────────────────────────────────────────────
  const totalCandidates   = candidates.length;
  const totalJDs          = jobDescriptions.length;
  const activeJDs         = jobDescriptions.filter(jd => jd?.is_active).length;
  const selectedCount     = candidates.filter(c => c?.overallStatus === 'Selected').length;
  const rejectedCount     = candidates.filter(c => REJECTED_STATUSES.includes(c?.overallStatus)).length;
  const interviewCount    = scheduledCandidates.length;
  const screeningPassCount = candidates.filter(c => ['ScreeningTest_Passed', 'ReScreeningTest_Passed'].includes(c?.overallStatus)).length;
  const newCount          = candidates.filter(c => c?.overallStatus === 'NewCandidate').length;

  const conversionRate = totalCandidates > 0 ? Math.round((selectedCount / totalCandidates) * 100) : 0;
  const rejectionRate  = totalCandidates > 0 ? Math.round((rejectedCount / totalCandidates) * 100) : 0;

  const errorBox = (msg) => (
    <Alert severity="error" sx={{ borderRadius: `${RADIUS.md}px` }} action={<Button color="inherit" size="small" onClick={handleRetry}>Retry</Button>}>{msg}</Alert>
  );
  const loadingBox = (msg) => (
    <EmptyState>
      <Typography sx={{ fontFamily: FONT_BODY, color: '#8B7DB5', fontSize: TYPE.body, lineHeight: 1.5 }}>{msg}</Typography>
    </EmptyState>
  );
  const partialWarning = failedJobIds.length > 0 && !errorCandidates && (
    <Alert severity="warning" sx={{ mb: 2, borderRadius: `${RADIUS.md}px` }} action={<Button color="inherit" size="small" onClick={handleRetry}>Retry</Button>}>
      Candidates could not be loaded for: {failedJobIds.join(', ')}
    </Alert>
  );

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={recruiterTheme}>
    <ReportsWrapper>

      {/* ── Global Overview Stats ── */}
      <StatsRow>
        <StatCard gradient="linear-gradient(135deg,#6750A4,#4F378B)" shadow="rgba(103,80,164,0.28)">
          <StatText>
            <StatNum>{loadingCandidates ? '…' : totalCandidates}</StatNum>
            <StatLbl>Total Candidates</StatLbl>
          </StatText>
          <StatIconWrap><PeopleAltIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#1565C0,#0D47A1)" shadow="rgba(21,101,192,0.24)">
          <StatText>
            <StatNum>{loadingJobs ? '…' : totalJDs}</StatNum>
            <StatLbl>Total JDs</StatLbl>
            <StatSub>{activeJDs} active</StatSub>
          </StatText>
          <StatIconWrap><WorkIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#F57F17,#E65100)" shadow="rgba(245,127,23,0.24)">
          <StatText>
            <StatNum>{loadingCandidates ? '…' : newCount}</StatNum>
            <StatLbl>New Candidates</StatLbl>
          </StatText>
          <StatIconWrap><HourglassTopIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#00897B,#00695C)" shadow="rgba(0,137,123,0.24)">
          <StatText>
            <StatNum>{loadingCandidates ? '…' : screeningPassCount}</StatNum>
            <StatLbl>Screening Passed</StatLbl>
          </StatText>
          <StatIconWrap><AssessmentIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#1565C0,#283593)" shadow="rgba(21,101,192,0.22)">
          <StatText>
            <StatNum>{loadingCandidates ? '…' : interviewCount}</StatNum>
            <StatLbl>Interviews Scheduled</StatLbl>
          </StatText>
          <StatIconWrap><ScheduleIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#2E7D32,#1B5E20)" shadow="rgba(46,125,50,0.24)">
          <StatText>
            <StatNum>{loadingCandidates ? '…' : selectedCount}</StatNum>
            <StatLbl>Selected</StatLbl>
          </StatText>
          <StatIconWrap><CheckCircleOutlineIcon /></StatIconWrap>
        </StatCard>
        <StatCard gradient="linear-gradient(135deg,#B71C1C,#C62828)" shadow="rgba(183,28,28,0.22)">
          <StatText>
            <StatNum>{loadingCandidates ? '…' : rejectedCount}</StatNum>
            <StatLbl>Rejected</StatLbl>
          </StatText>
          <StatIconWrap><CancelOutlinedIcon /></StatIconWrap>
        </StatCard>
      </StatsRow>

      {/* ── Overview Grid ── */}
      {(totalCandidates > 0 || totalJDs > 0) && (
        <OverviewGrid>
          {/* Pipeline Health */}
          <OverviewCard>
            <CardTitle>
              <TrendingUpIcon sx={{ fontSize: 20, color: '#6750A4' }} />
              Pipeline Health
            </CardTitle>
            {[
              { label: 'New',              count: newCount,           color: '#E65100', bg: '#FFF3E0' },
              { label: 'Screening Passed', count: screeningPassCount, color: '#6750A4', bg: '#EDE7F6' },
              { label: 'Interviews',       count: interviewCount,     color: '#1565C0', bg: '#E3F2FD' },
              { label: 'Selected',         count: selectedCount,      color: '#2D7D46', bg: '#E6F4EA' },
              { label: 'Rejected',         count: rejectedCount,      color: '#B3261E', bg: '#FDECEA' },
            ].map(({ label, count, color }) => (
              <MiniBarWrap key={label}>
                <MiniBarLabel>{label}</MiniBarLabel>
                <MiniBar>
                  <MiniBarFill color={color} pct={totalCandidates > 0 ? (count / totalCandidates) * 100 : 0} />
                </MiniBar>
                <MiniBarVal color={color}>{count}</MiniBarVal>
              </MiniBarWrap>
            ))}
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F0EAF9', display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, background: '#E6F4EA', borderRadius: `${RADIUS.sm}px`, p: '12px 16px', textAlign: 'center' }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 800, fontSize: TYPE.section, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: '#2D7D46', mb: 0.5 }}>{conversionRate}%</Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: '#2D7D46', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Conversion Rate</Typography>
              </Box>
              <Box sx={{ flex: 1, background: '#FDECEA', borderRadius: `${RADIUS.sm}px`, p: '12px 16px', textAlign: 'center' }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 800, fontSize: TYPE.section, lineHeight: 1, fontVariantNumeric: 'tabular-nums', color: '#B3261E', mb: 0.5 }}>{rejectionRate}%</Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: '#B3261E', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Rejection Rate</Typography>
              </Box>
            </Box>
          </OverviewCard>

          {/* JD Overview */}
          <OverviewCard>
            <CardTitle>
              <BusinessCenterIcon sx={{ fontSize: 20, color: '#6750A4' }} />
              Job Description Overview
            </CardTitle>
            {[
              { label: 'Total JDs',       count: totalJDs,  color: '#6750A4', bg: '#EDE7F6' },
              { label: 'Active JDs',      count: activeJDs, color: '#2D7D46', bg: '#E6F4EA' },
              { label: 'Inactive JDs',    count: totalJDs - activeJDs, color: '#B3261E', bg: '#FDECEA' },
            ].map(({ label, count, color, bg }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, pb: 1.5, borderBottom: '1px solid #FAF7FF' }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.small, fontWeight: 600, color: '#49454F' }}>{label}</Typography>
                <Box sx={{ background: bg, color, fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.body, fontVariantNumeric: 'tabular-nums', minWidth: 40, height: 28, px: 1.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', borderRadius: `${RADIUS.sm}px` }}>
                  {count}
                </Box>
              </Box>
            ))}
            <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #F0EAF9' }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.overline, fontWeight: 700, color: '#8B7DB5', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                Candidates per JD
              </Typography>
              {jobDescriptions.slice(0, 4).map(jd => {
                const counts = statusCounts[jd._id] || {};
                const total = (counts.applicationCount || 0);
                return (
                  <MiniBarWrap key={jd._id}>
                    <MiniBarLabel title={jd.jdID} style={{ fontSize: TYPE.overline }}>
                      {jd.jdID}
                    </MiniBarLabel>
                    <MiniBar>
                      <MiniBarFill color="#6750A4" pct={totalCandidates > 0 ? (total / totalCandidates) * 100 : 0} />
                    </MiniBar>
                    <MiniBarVal>{total || '—'}</MiniBarVal>
                  </MiniBarWrap>
                );
              })}
              {jobDescriptions.length > 4 && (
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: TYPE.caption, color: '#8B7DB5', mt: 0.5 }}>
                  +{jobDescriptions.length - 4} more JDs
                </Typography>
              )}
            </Box>
          </OverviewCard>
        </OverviewGrid>
      )}

      {/* ── Tabs ── */}
      <StyledTabsWrap>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons={false}
          sx={{
            '& .MuiTab-root': {
              fontFamily: FONT_BODY, fontWeight: 700, fontSize: TYPE.small,
              textTransform: 'none', color: '#8B7DB5', borderRadius: `${RADIUS.sm}px`, minHeight: 40,
              px: 2, whiteSpace: 'nowrap',
              '&.Mui-selected': { color: '#4F378B', background: '#EDE7F6' },
            },
            '& .MuiTabs-indicator': { background: '#6750A4', borderRadius: 2 },
            '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
            minHeight: 40,
          }}
        >
          <Tab label="Tracker Sheet" />
          <Tab label="Summary Table" />
          <Tab label="Interviews Scheduled" />
          <Tab label="Candidate Pipeline" />
        </Tabs>
      </StyledTabsWrap>

      {/* ── Tab 0: Tracker Sheet ── */}
      {activeTab === 0 && (
        <div>
          {partialWarning}
          {errorJobs ? errorBox(errorJobs) : errorCandidates ? (
            errorBox(errorCandidates)
          ) : loadingCandidates ? (
            loadingBox('Loading candidates…')
          ) : candidates.length === 0 ? (
            <EmptyState>
              <Typography sx={emptyTitleSx}>No candidates yet</Typography>
              <Typography sx={emptyTextSx}>Candidates will appear here once profiles are added.</Typography>
            </EmptyState>
          ) : (
            <TableCard>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['#', 'Candidate ID', 'Candidate', 'Exp', 'CTC (Curr → Exp)', 'Notice', 'Location', 'Relocate', 'Remarks'].map((head) => (
                        <HeadCell key={head} align={headAlign(head)}>{head}</HeadCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentProfiles.map((candidate, index) => (
                      <BodyRow key={candidate._id || candidate.candidateID}>
                        <BodyCell align="center" sx={{ color: '#8B7DB5 !important', fontWeight: '700 !important', fontVariantNumeric: 'tabular-nums' }}>{index + 1 + (candidatePage - 1) * itemsPerPage}</BodyCell>
                        <BodyCell sx={{ fontWeight: '700 !important', color: '#6750A4 !important', whiteSpace: 'nowrap' }}>{candidate.candidateID || '—'}</BodyCell>
                        <BodyCell>
                          <CellText max={240} title={candidate.candidatename || ''} style={{ fontWeight: 600, fontSize: TYPE.body, color: '#3D2B6B' }}>{candidate.candidatename || '—'}</CellText>
                          <CellText max={240} title={candidate.candidateEmail || ''} style={{ fontSize: TYPE.caption, color: '#6B5B9A' }}>{candidate.candidateEmail || '—'}</CellText>
                          <CellText max={240} style={{ fontSize: TYPE.caption, color: '#8B7DB5', fontVariantNumeric: 'tabular-nums' }}>{candidate.phone || '—'}</CellText>
                        </BodyCell>
                        <BodyCell align="center" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{candidate.totalExperience || '—'}</BodyCell>
                        <BodyCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} title={`Current: ${candidate.currentCTC || '—'} · Expected: ${candidate.expectedCTC || '—'}`}>
                          {candidate.currentCTC || '—'} <span style={{ color: '#8B7DB5' }}>→</span> {candidate.expectedCTC || '—'}
                        </BodyCell>
                        <BodyCell sx={{ whiteSpace: 'nowrap' }}>{candidate.noticePeriod || '0'}</BodyCell>
                        <BodyCell>
                          <CellText max={160} title={candidate.presentLocation || ''}>{candidate.presentLocation || '—'}</CellText>
                          <CellText max={160} title={candidate.preferredLocation || ''} style={{ fontSize: TYPE.caption, color: '#8B7DB5' }}>Pref: {candidate.preferredLocation || '—'}</CellText>
                        </BodyCell>
                        <BodyCell sx={{ whiteSpace: 'nowrap' }}>{candidate.willingToRelocate || '—'}</BodyCell>
                        <BodyCell sx={{ fontSize: '12px !important', color: '#8B7DB5 !important' }}><CellText max={160} title={candidate.recruiterFeedback || ''}>{candidate.recruiterFeedback || '—'}</CellText></BodyCell>
                      </BodyRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Pagination itemsPerPage={itemsPerPage} currentPage={currentPage} totalPages={totalPages}
                  handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} />
              )}
            </TableCard>
          )}
        </div>
      )}

      {/* ── Tab 1: Summary Table ── */}
      {activeTab === 1 && (
        <div>
          {errorJobs ? (
            errorBox(errorJobs)
          ) : loadingJobs ? (
            loadingBox('Loading job descriptions…')
          ) : jobDescriptions.length === 0 ? (
            <EmptyState>
              <Typography sx={emptyTitleSx}>No job descriptions</Typography>
              <Typography sx={emptyTextSx}>No JDs found for your account.</Typography>
            </EmptyState>
          ) : (
            <TableCard>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['JD ID', 'Job Role', 'Date Opened', 'Expiry', 'Dept.', 'Positions', 'Shortlisted', 'In Progress', 'On Hold', 'Remarks'].map((head) => (
                        <HeadCell key={head} align={headAlign(head)}>{head}</HeadCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentJobDescriptions.map((jd) => {
                      const { shortlistedCount, inProgressCount, onHoldCount } = statusCounts[jd._id] || {};
                      return (
                        <BodyRow key={jd._id}>
                          <BodyCell sx={{ fontWeight: '700 !important', color: '#6750A4 !important', whiteSpace: 'nowrap' }}>{jd.jdID}</BodyCell>
                          <BodyCell sx={{ fontWeight: '600 !important' }}><CellText max={240} title={jd.jobRole || ''}>{jd.jobRole}</CellText></BodyCell>
                          <BodyCell sx={{ color: '#8B7DB5 !important', fontSize: '12px !important', whiteSpace: 'nowrap' }}>{fmtDate(jd.creation_time)}</BodyCell>
                          <BodyCell sx={{ color: jd.is_active ? '#2D7D46 !important' : '#B3261E !important', fontSize: '12px !important', whiteSpace: 'nowrap' }}>
                            {jd.is_active ? fmtDate(jd.expiration_time) : 'Expired'}
                          </BodyCell>
                          <BodyCell>{jd.department || '—'}</BodyCell>
                          <BodyCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>{jd.openPositions || '—'}</BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#2D7D46" bg="#E6F4EA">{shortlistedCount || 0}</MetricCell>
                          </BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#1565C0" bg="#E3F2FD">{inProgressCount || 0}</MetricCell>
                          </BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#E65100" bg="#FFF3E0">{onHoldCount || 0}</MetricCell>
                          </BodyCell>
                          <BodyCell sx={{ fontSize: '12px !important', color: '#8B7DB5 !important' }}><CellText title={jd.remarks || ''}>{jd.remarks || '—'}</CellText></BodyCell>
                        </BodyRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Pagination itemsPerPage={itemsPerPage} currentPage={currentPage} totalPages={totalPages}
                  handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} />
              )}
            </TableCard>
          )}
        </div>
      )}

      {/* ── Tab 2: Interviews Scheduled ── */}
      {activeTab === 2 && (
  <div>
    {partialWarning}
    {errorJobs || errorCandidates ? (
      errorBox(errorJobs || errorCandidates)
    ) : loadingCandidates ? (
      loadingBox('Loading scheduled interviews…')
    ) : totalItems === 0 ? (
      <EmptyState>
        <Typography sx={emptyTitleSx}>
          No interviews scheduled
        </Typography>
        <Typography sx={emptyTextSx}>
          No candidates currently match the "Scheduled" status filter.
        </Typography>
      </EmptyState>
    ) : (
      <>
        {Object.keys(filteredCandidates).map((jobId) => {
          const job = jobDescriptions.find((jd) => jd?.jdID === jobId);
          const candidatesInJob = filteredCandidates[jobId];
          
          return (
            <div key={jobId} style={{ marginBottom: 32 }}>
              <SectionTitle>
                <ScheduleIcon sx={{ fontSize: 20, color: '#6750A4' }} />
                {job ? `${job.jobRole} (${jobId})` : jobId}
                <CountPill>
                  {candidatesInJob.length} on this page
                </CountPill>
              </SectionTitle>

              <TableCard>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['#', 'Dept.', 'Candidate ID', 'Name', 'Status', 'Interviewer', 'Mode', 'Date & Time'].map((head) => (
                          <HeadCell key={head} align={headAlign(head)}>{head}</HeadCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Removed individual slice here to show all grouped candidates 
                          Pagination is now handled globally below */}
                      {candidatesInJob.map((candidate, index) => {
                        const interviewDetails = interviewers[candidate._id] || {};
                        const interviewData = interviewDetails.interviewData || {};
                        const chip = getStatusChip(candidate.overallStatus);
                        return (
                          <BodyRow key={candidate._id || candidate.candidateID}>
                            <BodyCell align="center" sx={{ color: '#8B7DB5 !important', fontWeight: '700 !important', fontVariantNumeric: 'tabular-nums' }}>{index + 1}</BodyCell>
                            <BodyCell>{job?.department || '—'}</BodyCell>
                            <BodyCell sx={{ fontWeight: '700 !important', color: '#6750A4 !important', whiteSpace: 'nowrap' }}>{candidate.candidateID}</BodyCell>
                            <BodyCell sx={{ fontWeight: '600 !important', whiteSpace: 'nowrap' }}>{candidate.candidatename}</BodyCell>
                            <BodyCell>
                              <StatusChip bg={chip.bg} fg={chip.fg}>
                                <StatusDot color={chip.dot} />
                                {(candidate.overallStatus || '').replace(/_/g, ' ')}
                              </StatusChip>
                            </BodyCell>
                            <BodyCell sx={{ fontSize: '12px !important' }}>
                              <CellText max={200} title={Array.isArray(interviewData.interviewers) ? interviewData.interviewers.join(', ') : ''}>
                                {Array.isArray(interviewData.interviewers) && interviewData.interviewers.length
                                  ? interviewData.interviewers.join(', ') : 'NA'}
                              </CellText>
                            </BodyCell>
                            <BodyCell>{interviewData.mode || 'NA'}</BodyCell>
                            <BodyCell sx={{ color: '#8B7DB5 !important', fontSize: '12px !important', whiteSpace: 'nowrap' }}>
                              {interviewData.dateAndTime && !Number.isNaN(new Date(interviewData.dateAndTime).getTime())
                                ? new Date(interviewData.dateAndTime).toLocaleString([], { 
                                    dateStyle: 'short', timeStyle: 'short' 
                                  })
                                : 'NA'}
                            </BodyCell>
                          </BodyRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TableCard>
            </div>
          );
        })}
        
        {/* Global Pagination for Tab 2 */}
        {totalPages > 1 && (
          <Box sx={{ mt: -1, mb: 3 }}>
            <Pagination 
              itemsPerPage={itemsPerPage} 
              currentPage={currentPage} 
              totalPages={totalPages}
              handlePrevPage={handlePrevPage} 
              handleNextPage={handleNextPage} 
            />
          </Box>
        )}
      </>
    )}
  </div>
)}

      {/* ── Tab 3: Candidate Pipeline ── */}
      {activeTab === 3 && (
        <div>
          {errorJobs ? (
            errorBox(errorJobs)
          ) : loadingJobs ? (
            loadingBox('Loading pipeline…')
          ) : jobDescriptions.length === 0 ? (
            <EmptyState>
              <Typography sx={emptyTitleSx}>No data yet</Typography>
            </EmptyState>
          ) : (
            <TableCard>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['JD ID', 'Job Role', 'Dept.', 'Applications', 'Shortlisted', 'In Progress', 'Rejected', 'On Hold', 'Remarks'].map((head) => (
                        <HeadCell key={head} align={headAlign(head)}>{head}</HeadCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentJDS.map((jd) => {
                      const { applicationCount, shortlistedCount, inProgressCount, rejectedCount, onHoldCount } = statusCounts[jd._id] || {};
                      return (
                        <BodyRow key={jd._id}>
                          <BodyCell sx={{ fontWeight: '700 !important', color: '#6750A4 !important', whiteSpace: 'nowrap' }}>{jd.jdID}</BodyCell>
                          <BodyCell sx={{ fontWeight: '600 !important' }}><CellText max={240} title={jd.jobRole || ''}>{jd.jobRole}</CellText></BodyCell>
                          <BodyCell>{jd.department || '—'}</BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#4F378B" bg="#EDE7F6">{applicationCount || '—'}</MetricCell>
                          </BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#2D7D46" bg="#E6F4EA">{shortlistedCount || '—'}</MetricCell>
                          </BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#1565C0" bg="#E3F2FD">{inProgressCount || '—'}</MetricCell>
                          </BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#B3261E" bg="#FDECEA">{rejectedCount || '—'}</MetricCell>
                          </BodyCell>
                          <BodyCell align="center">
                            <MetricCell color="#E65100" bg="#FFF3E0">{onHoldCount || '—'}</MetricCell>
                          </BodyCell>
                          <BodyCell sx={{ color: '#8B7DB5 !important', fontSize: '12px !important' }}>—</BodyCell>
                        </BodyRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalPages > 1 && (
                <Pagination itemsPerPage={itemsPerPage} currentPage={currentPage} totalPages={totalPages}
                  handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} />
              )}
            </TableCard>
          )}
        </div>
      )}
    </ReportsWrapper>
    </ThemeProvider>
  );
};

export default Reports;